import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  CHART_ACCOUNT_NATURE_LABELS,
  CHART_ACCOUNT_TYPE_LABELS,
  COST_CENTER_KIND_LABELS,
  type ChartAccount,
  type ChartAccountNature,
  type ChartAccountNode,
  type ChartAccountType,
  type CostCenter,
  type CostCenterKind,
  type CostCenterNode,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Árvores de classificação: centros de custo (ONDE gastou)
 * e plano de contas (O QUE gastou). São as duas dimensões obrigatórias de
 * todo lançamento, e por isso ganham tela própria em vez de virarem um select
 * cadastrado às pressas no meio do formulário.
 *
 * Sintético agrupa e nunca recebe lançamento; analítico é folha e é o único
 * que aparece nos selects do Financeiro, do Estoque e da folha.
 */

interface TreeNodeLike {
  id: string;
  code: string;
  name: string;
  level: number;
  isAnalytic: boolean;
  isActive: boolean;
  parentId: string | null;
}

function TreeRows<T extends TreeNodeLike & { children: T[] }>({
  nodes,
  collapsed,
  onToggle,
  onEdit,
  onRemove,
  onAddChild,
  badge,
  canEdit,
  canRemove,
  canCreate,
}: {
  nodes: T[];
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  onEdit: (node: T) => void;
  onRemove: (node: T) => void;
  onAddChild: (node: T) => void;
  badge?: (node: T) => string;
  canEdit: boolean;
  canRemove: boolean;
  canCreate: boolean;
}) {
  return (
    <>
      {nodes.map((node) => {
        const hasChildren = node.children.length > 0;
        const isCollapsed = collapsed.has(node.id);
        return (
          <div key={node.id}>
            <div className="tree__row" style={{ paddingLeft: 14 + node.level * 20 }}>
              {hasChildren ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  style={{ padding: 2, height: 22, minWidth: 22 }}
                  aria-label={isCollapsed ? `Expandir ${node.name}` : `Recolher ${node.name}`}
                  aria-expanded={!isCollapsed}
                  onClick={() => onToggle(node.id)}
                >
                  {isCollapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                </button>
              ) : (
                <span style={{ width: 22, display: 'inline-block' }} aria-hidden />
              )}

              <span className="tree__code">{node.code}</span>
              <span style={{ fontWeight: node.isAnalytic ? 500 : 700, fontSize: 13 }}>{node.name}</span>

              {!node.isAnalytic && <StatusBadge kind="mute">Sintética</StatusBadge>}
              {badge && <StatusBadge kind="info">{badge(node)}</StatusBadge>}
              {!node.isActive && <StatusBadge kind="mute">Inativa</StatusBadge>}

              <span style={{ flex: 1 }} />

              {/* Ações aparecem na linha sob o mouse ou com foco de teclado: a
                  árvore inteira com três botões por linha eram 362 na tela. */}
              <span className="row-actions">
                {canCreate && (
                  <Button
                    small
                    variant="ghost"
                    icon={Plus}
                    aria-label={`Criar abaixo de ${node.name}`}
                    onClick={() => onAddChild(node)}
                  />
                )}
                {canEdit && (
                  <Button
                    small
                    variant="ghost"
                    icon={Pencil}
                    aria-label={`Editar ${node.name}`}
                    onClick={() => onEdit(node)}
                  />
                )}
                {canRemove && (
                  <Button
                    small
                    variant="ghost"
                    icon={Trash2}
                    aria-label={`Excluir ${node.name}`}
                    onClick={() => onRemove(node)}
                  />
                )}
              </span>
            </div>

            {hasChildren && !isCollapsed && (
              <TreeRows
                nodes={node.children}
                collapsed={collapsed}
                onToggle={onToggle}
                onEdit={onEdit}
                onRemove={onRemove}
                onAddChild={onAddChild}
                badge={badge}
                canEdit={canEdit}
                canRemove={canRemove}
                canCreate={canCreate}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

/* ============================ Centros de custo =========================== */

export function CentrosCustoPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [parentFor, setParentFor] = useState<CostCenter | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<CostCenter | null>(null);

  const treeQuery = useQuery({
    queryKey: ['cost-centers', 'tree'],
    queryFn: () => api.get<{ data: CostCenterNode[] }>('/cost-centers/tree'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/cost-centers/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <WorkspaceHeader
        title="Centros de custo"
        actions={
          can('centros-custo:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo centro de custo
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {treeQuery.isPending && <LoadingState />}
        {treeQuery.isError && (
          <ErrorState message={errorMessage(treeQuery.error)} onRetry={() => void treeQuery.refetch()} />
        )}
        {treeQuery.data &&
          (treeQuery.data.data.length === 0 ? (
            <EmptyState
              title="Nenhum centro de custo"
              description="Crie os centros de custo (fundação, piscinas, administrativo…) para classificar o gasto."
            />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <TreeRows
                nodes={treeQuery.data.data}
                collapsed={collapsed}
                onToggle={toggle}
                onEdit={setEditing}
                onRemove={setRemoving}
                onAddChild={(node) => setParentFor(node)}
                badge={(node) => COST_CENTER_KIND_LABELS[node.kind]}
                canEdit={can('centros-custo:editar')}
                canRemove={can('centros-custo:excluir')}
                canCreate={can('centros-custo:criar')}
              />
            </div>
          ))}
      </div>

      <CostCenterForm
        open={creating || Boolean(editing) || Boolean(parentFor)}
        costCenter={editing}
        parent={parentFor}
        onClose={() => {
          setCreating(false);
          setEditing(null);
          setParentFor(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          setParentFor(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este centro de custo?"
        confirmLabel="Excluir"
        message={
          <>
            Só sai da lista quem não tem filhos nem lançamentos. Se a etapa já
            recebeu custo, deixe-a inativa: o histórico não se apaga.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function CostCenterForm({
  open,
  costCenter,
  parent,
  onClose,
  onSaved,
}: {
  open: boolean;
  costCenter: CostCenter | null;
  parent: CostCenter | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const flatQuery = useQuery({
    queryKey: ['cost-centers', 'flat'],
    queryFn: () => api.get<{ data: CostCenter[] }>('/cost-centers'),
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CostCenterKind>('OBRA');
  const [parentId, setParentId] = useState('');
  const [isAnalytic, setIsAnalytic] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (costCenter?.id ?? `new:${parent?.id ?? ''}`) : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setCode(costCenter?.code ?? '');
    setName(costCenter?.name ?? '');
    setKind(costCenter?.kind ?? parent?.kind ?? 'OBRA');
    setParentId(costCenter?.parentId ?? parent?.id ?? '');
    setIsAnalytic(costCenter?.isAnalytic ?? true);
    setIsActive(costCenter?.isActive ?? true);
    setNotes(costCenter?.notes ?? '');
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      costCenter ? api.patch(`/cost-centers/${costCenter.id}`, body) : api.post('/cost-centers', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const options = (flatQuery.data?.data ?? []).filter(
    (item) => !item.isAnalytic && item.id !== costCenter?.id,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={costCenter ? 'Editar centro de custo' : 'Novo centro de custo'}
      description={parent ? `Abaixo de ${parent.code} · ${parent.name}` : undefined}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={code.trim() === '' || name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                code: code.trim(),
                name: name.trim(),
                kind,
                parentId: parentId || null,
                isAnalytic,
                isActive,
                notes: notes.trim() || null,
              })
            }
          >
            {costCenter ? 'Salvar' : 'Criar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Código" htmlFor="cc-code" error={errors.code} hint="Ex.: 1.2 ou OBRA-PISC">
            <input
              id="cc-code"
              className="input input--mono"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          <Field label="Tipo" htmlFor="cc-kind">
            <select
              id="cc-kind"
              className="input"
              value={kind}
              onChange={(event) => setKind(event.target.value as CostCenterKind)}
            >
              {Object.entries(COST_CENTER_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nome" htmlFor="cc-name" error={errors.name}>
          <input
            id="cc-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Piscina de ondas — estrutura"
          />
        </Field>

        <Field label="Agrupador" htmlFor="cc-parent" error={errors.parentId}>
          <select
            id="cc-parent"
            className="input"
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
          >
            <option value="">Nível raiz</option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.code} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isAnalytic}
            onChange={(event) => setIsAnalytic(event.target.checked)}
          />
          Analítico — aceita lançamento
        </label>
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          Desmarque para criar um agrupador. Agrupador soma os filhos e nunca
          recebe lançamento direto.
        </p>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Ativo
        </label>

        <Field label="Observações" htmlFor="cc-notes">
          <textarea
            id="cc-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* ============================= Plano de contas =========================== */

export function PlanoContasPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ChartAccount | null>(null);
  const [parentFor, setParentFor] = useState<ChartAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<ChartAccount | null>(null);

  const treeQuery = useQuery({
    queryKey: ['chart-accounts', 'tree'],
    queryFn: () => api.get<{ data: ChartAccountNode[] }>('/chart-accounts/tree'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['chart-accounts'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/chart-accounts/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <WorkspaceHeader
        title="Plano de contas"
        actions={
          can('plano-contas:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Nova conta
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {treeQuery.isPending && <LoadingState />}
        {treeQuery.isError && (
          <ErrorState message={errorMessage(treeQuery.error)} onRetry={() => void treeQuery.refetch()} />
        )}
        {treeQuery.data &&
          (treeQuery.data.data.length === 0 ? (
            <EmptyState title="Plano de contas vazio" description="Crie os grupos de receita e despesa." />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <TreeRows
                nodes={treeQuery.data.data}
                collapsed={collapsed}
                onToggle={toggle}
                onEdit={setEditing}
                onRemove={setRemoving}
                onAddChild={(node) => setParentFor(node)}
                badge={(node) => CHART_ACCOUNT_NATURE_LABELS[node.nature]}
                canEdit={can('plano-contas:editar')}
                canRemove={can('plano-contas:excluir')}
                canCreate={can('plano-contas:criar')}
              />
            </div>
          ))}
      </div>

      <ChartAccountForm
        open={creating || Boolean(editing) || Boolean(parentFor)}
        account={editing}
        parent={parentFor}
        onClose={() => {
          setCreating(false);
          setEditing(null);
          setParentFor(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          setParentFor(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir esta conta?"
        confirmLabel="Excluir"
        message={
          <>
            Contas com filhas ou com lançamentos não podem ser excluídas — nesse
            caso, deixe-a inativa.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function ChartAccountForm({
  open,
  account,
  parent,
  onClose,
  onSaved,
}: {
  open: boolean;
  account: ChartAccount | null;
  parent: ChartAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const flatQuery = useQuery({
    queryKey: ['chart-accounts', 'flat'],
    queryFn: () => api.get<{ data: ChartAccount[] }>('/chart-accounts'),
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<ChartAccountType>('DESPESA');
  const [nature, setNature] = useState<ChartAccountNature>('CUSTO');
  const [parentId, setParentId] = useState('');
  const [isAnalytic, setIsAnalytic] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (account?.id ?? `new:${parent?.id ?? ''}`) : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setCode(account?.code ?? '');
    setName(account?.name ?? '');
    setType(account?.type ?? parent?.type ?? 'DESPESA');
    setNature(account?.nature ?? parent?.nature ?? 'CUSTO');
    setParentId(account?.parentId ?? parent?.id ?? '');
    setIsAnalytic(account?.isAnalytic ?? true);
    setIsActive(account?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      account ? api.patch(`/chart-accounts/${account.id}`, body) : api.post('/chart-accounts', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const options = (flatQuery.data?.data ?? []).filter(
    (item) => !item.isAnalytic && item.id !== account?.id,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={account ? 'Editar conta' : 'Nova conta'}
      description={parent ? `Abaixo de ${parent.code} · ${parent.name}` : undefined}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={code.trim() === '' || name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                code: code.trim(),
                name: name.trim(),
                type,
                nature,
                parentId: parentId || null,
                isAnalytic,
                isActive,
              })
            }
          >
            {account ? 'Salvar' : 'Criar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Código" htmlFor="ca-code" error={errors.code}>
            <input
              id="ca-code"
              className="input input--mono"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          <Field label="Tipo" htmlFor="ca-type">
            <select
              id="ca-type"
              className="input"
              value={type}
              onChange={(event) => setType(event.target.value as ChartAccountType)}
            >
              {Object.entries(CHART_ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nome" htmlFor="ca-name" error={errors.name}>
          <input
            id="ca-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Material de construção"
          />
        </Field>

        <Field label="Natureza" htmlFor="ca-nature" error={errors.nature}>
          <select
            id="ca-nature"
            className="input"
            value={nature}
            onChange={(event) => setNature(event.target.value as ChartAccountNature)}
          >
            {Object.entries(CHART_ACCOUNT_NATURE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Agrupador" htmlFor="ca-parent" error={errors.parentId}>
          <select
            id="ca-parent"
            className="input"
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
          >
            <option value="">Nível raiz</option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.code} · {item.name}
              </option>
            ))}
          </select>
        </Field>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isAnalytic}
            onChange={(event) => setIsAnalytic(event.target.checked)}
          />
          Analítica — aceita lançamento
        </label>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Ativa
        </label>
      </FormSection>
    </Modal>
  );
}
